import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import { ArrowDownIcon } from "@heroicons/react/24/solid";
import PreviewSharpIcon from "@mui/icons-material/PreviewSharp";
import {
  Dialog,
  Card,
  CardHeader,
  Input,
  Typography,
  Button,
  CardBody,
  Chip,
  CardFooter,
  IconButton,
  Tooltip,
  Select,
  Option,
  Checkbox,
} from "@material-tailwind/react";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import ExcelJS from "exceljs";
import { useAuthUserToken } from "./../../../hooks/useAuthUserToken";
import TableRowWithShowMore from "./../../../components/TableRowWithShowMore";
import TableSkeleton from "./../../../components/Loaders/TableSkeleton";
import { getPlatformRoleData } from "../../../helperFunctions/apiFunction";

const TABLE_HEAD_DATA = ["Page", "Create", "Read", "Update", "Delete"];
const TABLE_HEAD = [
  "Name",
  "Scope",
  "Type",
  "Description",
  "Status",
  "Action",
];
const TABLE_ROWS = [
  {
    name: "User",
    rolePermission: {
      userAdd: true,
      userList: true,
      userEdit: true,
      userDel: true,
    },
  },
  {
    name: "Role",
    rolePermission: {
      roleAdd: true,
      roleList: true,
      roleEdit: true,
      roleDel: true,
    },
  },
  {
    name: "Program",
    rolePermission: {
      programAdd: true,
      programList: true,
      programEdit: true,
      programDel: true,
    },
  },
  {
    name: "Client",
    rolePermission: {
      clientAdd: true,
      clientEdit: true,
      clientList: true,
      clientDel: true,
    },
  },
];
export default function RoleManagment() {
  const { refreshToken, rbac } = useAuthUserToken();
  const [templateData, setTemplateData] = useState([]);
  const [isLoading, setIsLoading] = useState(null);
  const [searchBy, setSearchBy] = useState("");
  const [sortBy, setSortBy] = useState("asc");
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [open, setOpen] = useState(false);

  // Excel Doc
  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Roles Data");
    const headers = [
      "S.No.",
      "Role Name",
      "Role Scope",
      "Role Type",
      "Role Description",
      "Status",
    ];
    worksheet.addRow(headers);
    templateData.forEach((row, index) => {
      worksheet.addRow([
        index + 1,
        row.roleName,
        row.roleScope,
        row.roleType,
        row.roleDescription,
        row.status,
      ]);
    });
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "roles_data.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  // End Excel Doc

  const handleOpen = (item) => {
    setFormData(item);
    setOpen((cur) => !cur);
  };
  // End Dialog Box Open and Close

  // On Page Load
  useEffect(() => {
    getPlatformRoleData(
      refreshToken,
      page,
      limit,
      sortBy,
      searchBy,
      status
    ).then((resp) => {
      if (resp?.code === 440)
        toast.error(resp?.message, {
          className: "text-[12px]",
        });
      setTotalPage(resp?.data?.totalPages);
      setTemplateData(resp?.data?.results);
      setIsLoading(false);
    });
  }, [page, limit, sortBy, searchBy, status]);
  // End On Page Load

  // Create Form Functionality
  const [formData, setFormData] = useState({
    roleName: "",
    roleScope: "",
    roleType: "",
    roleStatus: "",
    roleDescription: "",
    rolePermission: {
      userAdd: false,
      userEdit: false,
      userList: false,
      userDel: false,

      roleAdd: false,
      roleEdit: false,
      roleList: false,
      roleDel: false,

      programAdd: false,
      programEdit: false,
      programList: false,
      programDel: false,

      clientAdd: false,
      clientEdit: false,
      clientList: false,
      clientDel: false,
    },
  });

  return (
    <div className="app-main__inner !pl-20 ">
      <Card
        className="h-full w-full rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className=" bg-transparent rounded-none !overflow-visible"
        >
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between md:gap-8">
            <div>
              <Typography variant="h5" color="blue-gray">
                List of Roles
              </Typography>
              <Typography color="gray" className="mt-1 font-normal">
                View all default and custom roles and permissions
              </Typography>
            </div>
            <div className="flex flex-row md:flex-row items-center justify-between md:items-center gap-2 md:gap-8 mt-4 md:mt-0">
              <Button
                variant="outlined"
                size="sm"
                onClick={exportToExcel}
                loading={isLoading === null ? true : false}
              >
                <ArrowDownIcon strokeWidth={2} className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
              <Select
                color="blue"
                className="!border-t-0 !border-[#b0bec5]"
                label="Select Limit"
                onChange={(event) => {
                  setLimit(event);
                  setIsLoading(null);
                }}
                value={limit}
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={30}>30</Option>
                <Option value={40}>40</Option>
                <Option value={50}>50</Option>
              </Select>
              <Select
                color="purple"
                className="!border-t-0 !border-[#b0bec5]"
                label="Sort By"
                value={sortBy}
                onChange={(event) => {
                  setSortBy(event);
                  setIsLoading(null);
                }}
              >
                <Option value="asc">Ascending</Option>
                <Option value="desc">Descending</Option>
              </Select>
              <Select
                color="green"
                className="!border-t-0 !border-[#b0bec5]"
                animate={{
                  mount: { y: 0 },
                  unmount: { y: 25 },
                }}
                label="Status"
                value={status}
                onChange={(event) => {
                  setStatus(event);
                  setIsLoading(null);
                }}
              >
                <Option value="">All Data</Option>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </div>
            <div className="w-full md:w-auto">
              <Input
                label="Search"
                className="focus:!border-black focus:!border-2 focus:!border-t-0"
                value={searchBy}
                onChange={(e) => setSearchBy(e.target.value)}
                icon={<MagnifyingGlassIcon className="h-5 w-5" />}
              />
            </div>
          </div>
        </CardHeader>
        <CardBody className="overflow-scroll px-3 p-0">
          <table className="mt-4 w-full min-w-max table-auto text-left">
            <thead>
              <tr>
                {TABLE_HEAD.map((head, index) => (
                  <th
                    key={head}
                    className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-3 transition-colors hover:bg-blue-gray-50 w-32"
                  >
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="flex items-center justify-between gap-2 font-bold leading-none opacity-70"
                    >
                      {head}
                      {index !== TABLE_HEAD.length - 1 && (
                        <ChevronUpDownIcon
                          strokeWidth={2}
                          className="h-4 w-4"
                        />
                      )}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            {isLoading === null ? (
              <>
                <tbody>
                  <tr>
                    <td colSpan={TABLE_HEAD.length}>
                      <TableSkeleton />
                    </td>
                  </tr>
                </tbody>
              </>
            ) : (
              <>
                <tbody>
                  {isLoading === false && templateData?.length > 0 ? (
                    templateData.map(
                      (
                        {
                          roleName,
                          status,
                          roleDescription,
                          roleType,
                          roleScope,
                          rolePermission,
                          id,
                        },
                        index
                      ) => {
                        const isLast = index === templateData.length - 1;
                        const classes = isLast
                          ? "p-3"
                          : "p-3 border-b border-blue-gray-50";

                        return (
                          <tr key={`${id}-${index}`}>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {roleName}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {roleScope}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {roleType}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {roleDescription?.length > 40 ? (
                                  <TableRowWithShowMore
                                    data={roleDescription}
                                  />
                                ) : (
                                  roleDescription
                                )}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <div className="w-max">
                                <Chip
                                  variant="ghost"
                                  size="sm"
                                  value={
                                    status === "active" ? "Active" : "Inactive"
                                  }
                                  color={status === "active" ? "green" : "red"}
                                />
                              </div>
                            </td>
                            {rbac?.roleEdit ? (
                              <>
                                <td className={classes}>
                                  <Tooltip
                                    className="bg-blue-gray-500"
                                    content="Prevoew Access"
                                  >
                                    <IconButton
                                      variant="text"
                                      onClick={() =>
                                        handleOpen({
                                          roleName,
                                          status,
                                          roleDescription,
                                          roleType,
                                          roleScope,
                                          rolePermission,
                                          id,
                                        })
                                      }
                                    >
                                      <PreviewSharpIcon className="h-4 w-4" />
                                    </IconButton>
                                  </Tooltip>
                                </td>
                              </>
                            ) : null}
                          </tr>
                        );
                      }
                    )
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="flex flex-col items-center w-full">
                          <img
                            quality={100}
                            src="/assets/images/9276430.jpg"
                            className="w-[350px] mix-blend-darken"
                            alt="no-data-found"
                          />
                          <p className="text-[20px] font-medium">
                            No Data Found!
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </>
            )}
          </table>
        </CardBody>
        {isLoading === null ? null : (
          <>
            <CardFooter className="flex items-center justify-between border-t border-blue-gray-50 p-4">
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setIsLoading(null);
                  setPage(page - 1);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPage }, (_, index) => (
                  <IconButton
                    key={index + 1}
                    variant={index + 1 === page ? "outlined" : "text"}
                    size="sm"
                    onClick={() => {
                      setIsLoading(null);
                      setPage(index + 1);
                    }}
                    disabled={totalPage > 1 ? false : true}
                  >
                    {index + 1}
                  </IconButton>
                )).reduce((acc, button, index, array) => {
                  if (
                    totalPage > 4 &&
                    (index === 1 || index === array.length - 2)
                  ) {
                    acc.push(
                      <IconButton
                        key={`ellipsis-${index}`}
                        variant="text"
                        size="sm"
                        disabled
                      >
                        ...
                      </IconButton>
                    );
                  } else {
                    acc.push(button);
                  }
                  return acc;
                }, [])}
              </div>
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page + 1);
                  setIsLoading(null);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Next
              </Button>
            </CardFooter>
          </>
        )}
      </Card>

      {/* View Access Role Box */}
      <Dialog
        size="lg"
        open={open}
        handler={handleOpen}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <form>
          <Card className="h-full w-full">
            <CardBody className="flex flex-col gap-2">
              <Typography variant="h4" color="blue-gray">
                {formData?.roleName} || Preview Access
              </Typography>
              <table className="w-full min-w-max table-auto text-left mt-2">
                <thead>
                  <tr>
                    {TABLE_HEAD_DATA.map((head) => (
                      <th
                        key={head}
                        className="border-b border-blue-gray-100 bg-blue-gray-50 p-4"
                      >
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-semibold leading-none opacity-70"
                        >
                          {head}
                        </Typography>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {TABLE_ROWS.map(({ name, rolePermission }, rowIndex) => (
                    <tr
                      key={name}
                      className={`even:bg-blue-gray-50/50 row-${rowIndex}`}
                    >
                      <td className="p-2">
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                        >
                          {name}
                        </Typography>
                      </td>
                      {Object.entries(rolePermission).map(
                        ([permissionName]) => (
                          <td key={permissionName} className="p-1.5">
                            <Checkbox
                              id={`${rowIndex}-${permissionName}`}
                              name={permissionName}
                              ripple={true}
                              color="green"
                              checked={
                                formData?.rolePermission?.[permissionName] ||
                                false
                              }
                              className="cursor-not-allowed pointer-events-none"
                            />
                          </td>
                        )
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
            <CardFooter className="flex justify-end border-t-2 py-2">
              <Button
                variant="text"
                color="red"
                onClick={handleOpen}
                className="mr-1"
              >
                <span>Cancel</span>
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Dialog>
      {/* View Access Role Box */}
    </div>
  );
}
